import { createPopper } from '@popperjs/core'

// Almacena las instancias de popper y los elementos del tooltip asociados a cada elemento del DOM
const tooltipMap = new Map()

function createTooltipElement(content) {
  // Contenedor externo posicionado instantáneamente por Popper (evita animación de traslación)
  const wrapper = document.createElement('div')
  wrapper.style.position = 'fixed'
  wrapper.style.top = '0'
  wrapper.style.left = '0'
  wrapper.style.zIndex = '50'
  wrapper.style.pointerEvents = 'none'
  wrapper.setAttribute('role', 'tooltip')

  // Elemento interno con estilos y animación de escala/opacidad
  const inner = document.createElement('div')
  inner.className =
    'px-2.5 py-1.5 text-xs font-medium text-slate-100 bg-slate-900/95 dark:bg-slate-950/95 border border-slate-800 dark:border-slate-800/40 rounded-md shadow-lg shadow-black/20 pointer-events-none opacity-0 scale-95 transition-all duration-100 ease-out'
  inner.innerText = content

  wrapper.appendChild(inner)
  return { wrapper, inner }
}

function showTooltip(el, binding) {
  const data = tooltipMap.get(el)
  if (!data) return
  if (data.visible) return

  const content = typeof binding.value === 'string' ? binding.value : binding.value?.content
  if (!content) return

  // Crear elementos del tooltip
  const { wrapper, inner } = createTooltipElement(content)
  document.body.appendChild(wrapper)
  data.tooltipEl = wrapper
  data.innerEl = inner

  // Determinar la ubicación (ej: v-tooltip.bottom o v-tooltip="{ placement: 'bottom' }")
  let placement = 'top'
  if (typeof binding.value === 'object') {
    placement = binding.value.placement || 'top'
  } else {
    const modifiers = Object.keys(binding.modifiers)
    if (modifiers.length > 0) {
      placement = modifiers[0]
    }
  }

  // Instanciar Popper para el posicionamiento
  const popperInstance = createPopper(el, wrapper, {
    strategy: 'fixed',
    placement: placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8], // Desplazamiento de 8px respecto al botón
        },
      },
      {
        name: 'preventOverflow',
        options: {
          boundary: 'viewport', // Evita que se salga de la pantalla
        },
      },
    ],
  })

  data.popperInstance = popperInstance
  data.visible = true

  // Activar la animación de entrada en el siguiente frame de renderizado
  requestAnimationFrame(() => {
    // Forzar reflow
    inner.offsetHeight
    inner.classList.remove('opacity-0', 'scale-95')
    inner.classList.add('opacity-100', 'scale-100')
  })
}

function hideTooltip(el, instant = false) {
  const data = tooltipMap.get(el)
  if (!data || !data.visible) return

  const wrapper = data.tooltipEl
  const inner = data.innerEl
  const popperInstance = data.popperInstance

  if (instant) {
    if (wrapper && wrapper.parentNode) {
      wrapper.parentNode.removeChild(wrapper)
    }
    if (popperInstance) {
      popperInstance.destroy()
    }
    data.tooltipEl = null
    data.innerEl = null
    data.popperInstance = null
    data.visible = false
    return
  }

  if (inner && wrapper) {
    inner.classList.remove('opacity-100', 'scale-100')
    inner.classList.add('opacity-0', 'scale-95')

    // Remover del DOM y destruir popper una vez termine la transición del elemento interno
    inner.addEventListener('transitionend', function handler() {
      inner.removeEventListener('transitionend', handler)
      if (wrapper.parentNode) {
        wrapper.parentNode.removeChild(wrapper)
      }
      if (popperInstance) {
        popperInstance.destroy()
      }
    })
  } else {
    if (popperInstance) {
      popperInstance.destroy()
    }
  }

  data.tooltipEl = null
  data.innerEl = null
  data.popperInstance = null
  data.visible = false
}

export default {
  mounted(el, binding) {
    const content = typeof binding.value === 'string' ? binding.value : binding.value?.content
    if (!content) return

    const data = {
      visible: false,
      tooltipEl: null,
      innerEl: null,
      popperInstance: null,
      binding: binding,
      onMouseEnter: () => showTooltip(el, data.binding),
      onMouseLeave: () => hideTooltip(el),
      onFocusIn: () => showTooltip(el, data.binding),
      onFocusOut: () => hideTooltip(el),
      onClick: () => hideTooltip(el, true),
    }

    tooltipMap.set(el, data)

    el.addEventListener('mouseenter', data.onMouseEnter)
    el.addEventListener('mouseleave', data.onMouseLeave)
    el.addEventListener('focusin', data.onFocusIn)
    el.addEventListener('focusout', data.onFocusOut)
    el.addEventListener('click', data.onClick)
  },

  updated(el, binding) {
    const data = tooltipMap.get(el)
    const content = typeof binding.value === 'string' ? binding.value : binding.value?.content

    if (!content) {
      if (data) {
        hideTooltip(el)
        el.removeEventListener('mouseenter', data.onMouseEnter)
        el.removeEventListener('mouseleave', data.onMouseLeave)
        el.removeEventListener('focusin', data.onFocusIn)
        el.removeEventListener('focusout', data.onFocusOut)
        el.removeEventListener('click', data.onClick)
        tooltipMap.delete(el)
      }
      return
    }

    if (!data) {
      const newData = {
        visible: false,
        tooltipEl: null,
        innerEl: null,
        popperInstance: null,
        binding: binding,
        onMouseEnter: () => showTooltip(el, newData.binding),
        onMouseLeave: () => hideTooltip(el),
        onFocusIn: () => showTooltip(el, newData.binding),
        onFocusOut: () => hideTooltip(el),
        onClick: () => hideTooltip(el, true),
      }
      tooltipMap.set(el, newData)
      el.addEventListener('mouseenter', newData.onMouseEnter)
      el.addEventListener('mouseleave', newData.onMouseLeave)
      el.addEventListener('focusin', newData.onFocusIn)
      el.addEventListener('focusout', newData.onFocusOut)
      el.addEventListener('click', newData.onClick)
    } else {
      data.binding = binding
      if (data.visible && data.innerEl) {
        data.innerEl.innerText = content
        if (data.popperInstance) {
          data.popperInstance.update()
        }
      }
    }
  },

  unmounted(el) {
    const data = tooltipMap.get(el)
    if (!data) return

    // Eliminar inmediatamente sin transición en el desmontaje
    const wrapper = data.tooltipEl
    const popperInstance = data.popperInstance

    if (wrapper && wrapper.parentNode) {
      wrapper.parentNode.removeChild(wrapper)
    }
    if (popperInstance) {
      popperInstance.destroy()
    }

    el.removeEventListener('mouseenter', data.onMouseEnter)
    el.removeEventListener('mouseleave', data.onMouseLeave)
    el.removeEventListener('focusin', data.onFocusIn)
    el.removeEventListener('focusout', data.onFocusOut)
    el.removeEventListener('click', data.onClick)

    tooltipMap.delete(el)
  },
}
